import 'date-fns';
import React from 'react';
import {Box, Grid, Typography, makeStyles, TextField} from '@material-ui/core';
import {observer} from 'mobx-react-lite';
import {ParameterStore} from '@/Core/mobx/ParameterStore/ParameterStore';
import {z} from 'zod';
import {Controller, useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useCommonModalStyles} from '@/Agent/Quotes/InstantAppModalPages/common/modalStyles';
import FixedNavigation from '@/Agent/Quotes/InstantAppModalPages/common/FixedNavigation';
import StickyTopNavigation from '@/Agent/Quotes/InstantAppModalPages/common/StickyTopNavigation';
import {ContactFormStore} from '@/Agent/ContactForm/ContactFormStore/ContactFormStore';
import {Autocomplete} from '@material-ui/lab';
import {EMC_COUNTRY_LIST, STATE_OPTIONS} from '@/Agent/constants';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
interface BirthplaceProps {
    parameterStore: ParameterStore;
    contactFormStore: ContactFormStore;
    onExit: () => void;
    onNext: () => void;
    onBack?: () => void;
}
const useStyles = makeStyles(() => ({
    container: {
        maxWidth: 700,
        width: '100%',
        position: 'relative',
        paddingBottom: 113,
        margin: '0 auto',
    },
    title: {
        marginBottom: 40,
        marginTop: 40,
        fontFamily: 'LoraSemiBold',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: 40,
        lineHeight: '110%',
        color: '#3F51B5',
        opacity: 0.9,
    },
    form: {
        display: 'flex',
        rowGap: 24,
    },
}));

const formSchema = z
    .object({
        birthCountry: z.string().min(1, 'Birth Country is required'),
        birthState: z.string().nullable().optional(),
    })
    .superRefine((data, ctx) => {
        if (data.birthCountry === 'United States of America') {
            if (!data.birthState) {
                ctx.addIssue({
                    path: ['birthState'],
                    message: 'Birth State is required',
                    code: z.ZodIssueCode.custom,
                });
            }
        } else {
            data.birthState = null;
        }
    });

const Birthplace: React.FC<BirthplaceProps> = observer(({onNext, onBack, onExit, parameterStore, contactFormStore}) => {
    const classes = useStyles();
    const {approval, onValueChangedPreApproval} = parameterStore;
    const commonClasses = useCommonModalStyles();
    const {
        control,
        watch,
        formState: {isSubmitting, isValid},
        handleSubmit,
    } = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            birthCountry: EMC_COUNTRY_LIST.includes(approval.birthCountry.value) ? approval.birthCountry.value : '',
            birthState: STATE_OPTIONS.includes(approval.birthState.value) ? approval.birthState.value : null,
        },
    });
    const onSubmit = (data: any) => {
        const birthState = data.birthCountry === 'United States of America' ? data.birthState : null;
        onValueChangedPreApproval({...approval.birthCountry, name: 'birthCountry'}, data.birthCountry);
        onValueChangedPreApproval({...approval.birthState, name: 'birthState'}, birthState);
        contactFormStore.saveForm();
        onNext();
    };

    const watchBirthCountry = watch('birthCountry');

    return (
        <Box className={classes.container}>
            <StickyTopNavigation onExit={onExit} />
            <Typography className={classes.title}>Where were you born?</Typography>
            <Grid container spacing={2} component="form" onSubmit={handleSubmit(onSubmit)} className={classes.form}>
                <Grid item xs={watchBirthCountry === 'United States of America' ? 6 : 12}>
                    <Typography className={commonClasses.label}>Birth Country</Typography>
                    <Controller
                        name="birthCountry"
                        control={control}
                        render={({field, fieldState: {error}}) => (
                            <Autocomplete
                                {...field}
                                id="birth-country"
                                clearOnBlur
                                options={EMC_COUNTRY_LIST}
                                value={field.value || null}
                                onChange={(_, newValue) => field.onChange(newValue)}
                                className={commonClasses.autocomplete}
                                popupIcon={<ExpandMoreIcon />}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        aria-label="Birth Country"
                                        variant="outlined"
                                        error={!!error}
                                        classes={{root: commonClasses.input}}
                                        helperText={error?.message}
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>
                {watchBirthCountry === 'United States of America' && (
                    <Grid item xs={12} md={6}>
                        <Typography className={commonClasses.label}>Birth State</Typography>
                        <Controller
                            name="birthState"
                            control={control}
                            render={({field, fieldState: {error}}) => (
                                <Autocomplete
                                    {...field}
                                    id="birth-state"
                                    clearOnBlur
                                    options={STATE_OPTIONS}
                                    value={field.value || null}
                                    onChange={(_, newValue) => field.onChange(newValue)}
                                    className={commonClasses.autocomplete}
                                    popupIcon={<ExpandMoreIcon />}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            aria-label="Birth State"
                                            variant="outlined"
                                            error={!!error}
                                            classes={{root: commonClasses.input}}
                                            helperText={error?.message}
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                )}
                <FixedNavigation onBack={onBack} isNextDisabled={!isValid} isLoading={isSubmitting} />
            </Grid>
        </Box>
    );
});
export default Birthplace;
